/**
 * Sistema de autorização por permission keys.
 */

// Definição de todas as rotas do sistema
export const ROUTES = {
  // Públicas
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  INVITE: '/invite',

  // Dashboard
  DASHBOARD: '/dashboard',
  ONBOARDING: '/onboarding',

  // Gestão
  APPOINTMENTS: '/appointments',
  CUSTOMERS: '/customers',
  PROFESSIONALS: '/professionals',
  WORKS: '/works',
  STOCK: '/stock',

  // Financeiro
  BILLING: '/billing',
  REPORTS: '/reports',

  // Sistema
  NOTIFICATIONS: '/notifications',
  SUBSCRIPTION: '/subscription',
  PAYMENT_CONFIG: '/payment-config',
  SETTINGS: '/settings',
  IAM: '/access-control',
} as const;

export const ROUTE_REQUIRED_PERMISSION: Partial<Record<string, string>> = {
  [ROUTES.DASHBOARD]: 'dashboard.read',
  [ROUTES.ONBOARDING]: 'settings.read',
  [ROUTES.APPOINTMENTS]: 'appointments.read',
  [ROUTES.CUSTOMERS]: 'customers.read',
  [ROUTES.PROFESSIONALS]: 'professionals.read',
  [ROUTES.WORKS]: 'work.read',
  [ROUTES.STOCK]: 'stock.read',
  [ROUTES.BILLING]: 'billing.read',
  [ROUTES.REPORTS]: 'reports.read',
  [ROUTES.NOTIFICATIONS]: 'notifications.read',
  [ROUTES.SUBSCRIPTION]: 'subscription.read',
  [ROUTES.PAYMENT_CONFIG]: 'payment_config.read',
  [ROUTES.SETTINGS]: 'settings.read',
  [ROUTES.IAM]: 'iam.manage',
};

/**
 * Verifica se o usuário pode acessar à área de estoque.
 * Quem tem `stock.write` (gestão de estoque) acede sempre; outros precisam de `stock.read` + configuração do estabelecimento.
 */
export const canAccessStock = (
  user: { id?: string; permissions?: string[] } | null,
  establishment: {
    stockPermissionConfig?: { allowTakeOrReplace?: string; allowedUserIds?: string[] } | null;
  } | null
): boolean => {
  if (!user) return false;
  const isAdmin = user.permissions?.includes('stock.write') === true;
  if (isAdmin) return true;
  if (user.permissions?.includes('stock.read') !== true) return false;
  const config = establishment?.stockPermissionConfig;
  if (!config) return false;
  return (
    config.allowTakeOrReplace === 'all' ||
    (Array.isArray(config.allowedUserIds) && config.allowedUserIds.includes(user.id ?? ''))
  );
};

/**
 * Verifica se o usuário tem permissão para acessar a uma rota
 */
export const hasRoutePermission = (route: string, permissionKeys?: string[]): boolean => {
  const requiredPermission = ROUTE_REQUIRED_PERMISSION[route];

  if (!requiredPermission) return true;
  return Array.isArray(permissionKeys) && permissionKeys.includes(requiredPermission);
};

/**
 * Retorna todas as rotas permitidas a partir das permission keys efetivas do usuário.
 */
export const getAllowedRoutes = (permissionKeys: string[] | undefined): string[] => {
  if (!Array.isArray(permissionKeys)) return [];
  return Object.keys(ROUTE_REQUIRED_PERMISSION).filter((route) => {
    const requiredPermission = ROUTE_REQUIRED_PERMISSION[route];
    return requiredPermission ? permissionKeys.includes(requiredPermission) : true;
  });
};

/**
 * Indica se o usuário ainda não tem permissões IAM efetivas (lista vazia ou ausente).
 * Usado para exibir estado “em desenvolvimento” até o tenant atribuir grupos/permissões.
 */
export const isEffectivePermissionsEmpty = (permissionKeys: string[] | undefined): boolean => {
  return !Array.isArray(permissionKeys) || permissionKeys.length === 0;
};

/**
 * O usuário tem todas as permission keys pedidas (AND). Usado em guards adicionais.
 */
export const hasAllEffectivePermissions = (
  permissionKeys: string[] | undefined,
  required: readonly string[]
): boolean => {
  if (required.length === 0) return true;
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) return false;
  return required.every((key) => permissionKeys.includes(key));
};

/**
 * Permissões mínimas quando a API devolve 403 em GET /user/me mas mantemos sessão para regularizar assinatura.
 * Alinhado a `createNeedsSubscriptionUser` e a ROUTES de subscription/settings/onboarding.
 */
export const SUBSCRIPTION_FALLBACK_PERMISSION_KEYS = [
  'subscription.read',
  'settings.read',
] as const;

const MENU_ITEMS_NEEDS_SUBSCRIPTION = [
  {
    title: 'Principal',
    items: [{ name: 'Primeiros passos', href: ROUTES.ONBOARDING, icon: 'onboarding' }],
  },
  {
    title: 'Sistema',
    items: [
      { name: 'Assinatura', href: ROUTES.SUBSCRIPTION, icon: 'subscription' },
      { name: 'Configurações', href: ROUTES.SETTINGS, icon: 'settings' },
    ],
  },
];

/**
 * Estrutura base do menu (antes do filtro por permissões).
 * Com `needsSubscription === true`, retorna apenas Assinatura e Configurações.
 */
const getBaseMenuSections = (needsSubscription?: boolean) => {
  if (needsSubscription === true) {
    return MENU_ITEMS_NEEDS_SUBSCRIPTION;
  }
  return [
    {
      title: 'Principal',
      items: [
        { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: 'dashboard' },
        { name: 'Primeiros passos', href: ROUTES.ONBOARDING, icon: 'onboarding' },
      ],
    },
    {
      title: 'Gestão',
      items: [
        { name: 'Agendamentos', href: ROUTES.APPOINTMENTS, icon: 'appointment' },
        { name: 'Clientes', href: ROUTES.CUSTOMERS, icon: 'customers' },
        { name: 'Profissionais', href: ROUTES.PROFESSIONALS, icon: 'professionals' },
        { name: 'Serviços', href: ROUTES.WORKS, icon: 'works' },
        { name: 'Estoque', href: ROUTES.STOCK, icon: 'stock' },
      ],
    },
    {
      title: 'Financeiro',
      items: [
        { name: 'Financeiro', href: ROUTES.BILLING, icon: 'billing' },
        { name: 'Relatórios', href: ROUTES.REPORTS, icon: 'reports' },
      ],
    },
    {
      title: 'Sistema',
      items: [
        { name: 'Notificações', href: ROUTES.NOTIFICATIONS, icon: 'notifications' },
        { name: 'Acessos', href: ROUTES.IAM, icon: 'users' },
        { name: 'Assinatura', href: ROUTES.SUBSCRIPTION, icon: 'subscription' },
        { name: 'Config. Pagamento', href: ROUTES.PAYMENT_CONFIG, icon: 'billing' },
        { name: 'Configurações', href: ROUTES.SETTINGS, icon: 'settings' },
      ],
    },
  ];
};

/**
 * Monta as seções do menu filtrando cada item pelas permission keys do usuário.
 */
export const getMenuItemsForPermissions = (
  permissionKeys: string[] | undefined,
  needsSubscription?: boolean
) => {
  const baseMenu = getBaseMenuSections(needsSubscription);
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
    return baseMenu;
  }

  return baseMenu
    .map((section) => ({
      ...section,
      items: (section.items as Array<{ href: string }>).filter((item) => {
        const requiredPermission = ROUTE_REQUIRED_PERMISSION[item.href];
        return !requiredPermission || permissionKeys.includes(requiredPermission);
      }),
    }))
    .filter((section) => section.items.length > 0);
};
